import React, { useState, useEffect, useRef, useCallback } from 'react'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { CircularProgress, TextField } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import { useFormContext } from 'react-hook-form'
import MpciApiClient from '../services/MpciApiClient'
import UaeMpciApiUrls from 'src/apiUrls/UaeMpciApiUrls'
import withMandatoryHighlight from './withMandatoryHighlight'

const useStyles = makeStyles(() => ({
  asterisk: { color: '#f44336' },
    autocomplete: {
      '& .MuiFilledInput-root .MuiAutocomplete-input': {
        padding: '10px 4px 10px 6px'
      },
      '& .MuiFilledInput-underline:before, & .MuiFilledInput-underline:after': {
        display: 'none'
      }
    }
}))

// Simple session cache — avoids repeated API calls for the same codeType+searchText
const sessionCache = {}
function getCacheKey(codeType, text) { return `${codeType}||${text}` }

function CodeMaintenanceSearchFieldBase({
  codeType,
  name,
  value: valueProp,
  onChange,
  onSelect,
  disabled,
  label,
  required,
  helperText,
  size = 'small',
  separator = ' : '
}) {
  const classes = useStyles()
  const formContext = useFormContext()
  const isControlled = valueProp !== undefined

  const currentValue = isControlled
    ? (valueProp || '')
    : (name ? (formContext?.watch(name) || '') : '')

  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedOption, setSelectedOption] = useState(null)
  const debounceRef = useRef(null)
  const mountedRef = useRef(true)

  useEffect(() => { return () => { mountedRef.current = false } }, [])

  // Resolve display label for current value on mount / value change
  useEffect(() => {
    if (!currentValue) {
      setSelectedOption(null)
      return
    }
    // Check if already in options
    const found = options.find((o) => o.code === currentValue)
    if (found) {
      setSelectedOption(found)
      return
    }
    // Fetch from API to resolve label
    const cacheKey = getCacheKey(codeType, currentValue)
    if (sessionCache[cacheKey]) {
      const cached = sessionCache[cacheKey]
      const match = cached.find((o) => o.code === currentValue)
      if (match) { setSelectedOption(match); setOptions(cached); return }
    }
    MpciApiClient.post(
      UaeMpciApiUrls.CODE_MAINTENANCE_SEARCH,
      { codeType, searchText: currentValue },
      (res) => {
        if (!mountedRef.current) return
        const list = Array.isArray(res) ? res : []
        sessionCache[cacheKey] = list
        setOptions(list)
        const match = list.find((o) => o.code === currentValue)
        setSelectedOption(match || { code: currentValue, description: '' })
      },
      () => {
        if (!mountedRef.current) return
        setSelectedOption({ code: currentValue, description: '' })
      }
    )
  }, [currentValue, codeType]) // eslint-disable-line react-hooks/exhaustive-deps

  const doSearch = useCallback((text) => {
    const cacheKey = getCacheKey(codeType, text)
    if (sessionCache[cacheKey]) {
      setOptions(sessionCache[cacheKey])
      return
    }
    setLoading(true)
    MpciApiClient.post(
      UaeMpciApiUrls.CODE_MAINTENANCE_SEARCH,
      { codeType, searchText: text ?? '' },
      (res) => {
        if (!mountedRef.current) return
        setLoading(false)
        const list = Array.isArray(res) ? res : []
        sessionCache[cacheKey] = list
        setOptions(list)
      },
      () => {
        if (!mountedRef.current) return
        setLoading(false)
        setOptions([])
      }
    )
  }, [codeType])

  function getOptionLabel(opt) {
    if (!opt) return ''
    if (typeof opt === 'string') return opt
    return opt.description ? `${opt.code}${separator}${opt.description}` : opt.code
  }

  function handleChange(_, option) {
    const code = option ? option.code : ''
    setSelectedOption(option)
    if (onSelect) onSelect(option)
    if (isControlled) {
      if (onChange) onChange({ target: { value: code } })
    } else if (name) {
      if (formContext) formContext.setValue(name, code, { shouldValidate: true })
    }
  }

  function handleInputChange(_, newInput, reason) {
    if (reason === 'input') {
      clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => doSearch(newInput), 300)
    }
    if (reason === 'clear') {
      setSelectedOption(null)
      if (onSelect) onSelect(null)
      if (isControlled) {
        if (onChange) onChange({ target: { value: '' } })
      } else if (name) {
        if (formContext) formContext.setValue(name, '', { shouldValidate: true })
      }
    }
  }

  function handleOpen() {
    doSearch('')
  }

  const fieldError = !isControlled && name
    ? formContext?.formState?.errors?.[name]
    : undefined

    return (
    <Autocomplete
      className={classes.autocomplete}
      style={{ width: '100%' }}
      options={options}
      getOptionLabel={getOptionLabel}
      getOptionSelected={(opt, val) => opt?.code === val?.code}
      value={selectedOption}
      onChange={handleChange}
      onInputChange={handleInputChange}
      onOpen={handleOpen}
      disabled={disabled}
      loading={loading}
      noOptionsText='No results'
      clearOnBlur={false}
      popupIcon={<ArrowDropDownIcon color='primary' />}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          label={label}
          required={required}
          helperText={fieldError?.message || helperText}
          error={!!fieldError}
          size={size}
                    variant='filled'
          InputLabelProps={{
            ...params.InputLabelProps,
            classes: {
              root: 'ng-form-control-label',
              focused: 'ng-form-control-label-filled-focused',
              error: 'ng-form-control-label-error',
              asterisk: classes.asterisk
            }
          }}
                    InputProps={{
            ...params.InputProps,
            classes: {
              root: `ng-form-control mpci-highlight-target${label ? '' : ' no-label'}`,
              underline: 'ng-form-control-filled',
              focused: 'ng-form-control-filled-focused',
              error: 'ng-form-control-filled-error'
            },
            style: { backgroundColor: '#ffffff', border: '1px solid #5f5753', borderRadius: 4 },
            disableUnderline: true,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={14} style={{ marginRight: 4 }} /> : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
    />
  )
}

export default withMandatoryHighlight(CodeMaintenanceSearchFieldBase)
